/**
 * Reseller Users tab (TMMPDM-0015) — 0004's MembersPanel shape: a debounced search, one
 * generous page (size 100, no paging UI), Email · Name · Role · Status · MFA · Password.
 * Invite user… (202 → "Invitation sent." and the Invitations tab refreshes); per row a role
 * select, Suspend / Reactivate, and Remove (confirm). Mutations key on the membership `id`.
 * Every failure renders the backend message in the panel alert — a 409
 * `RESELLER_CONSOLE_NOT_CONFIGURED` as INFO with `resellers.users.noConsole`, since tmm has
 * no reseller console yet and that answer is expected.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { PeopleTableContainer, PeopleToolbar } from '../partners/peopleLeafLayout';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { tenantStatusLabel } from '@/domain/registry';
import { StatusChip } from '@/status';
import {
  changeResellerUserRole,
  listResellerUsers,
  removeResellerUser,
  setResellerUserStatus,
} from '@/api/saas/resellers';
import { ApiError, errorCopy } from '@/api/errors';
import { RESELLER_ROLES, type ResellerMemberDto, type ResellerRole } from '@/api/saas/types';
import { InviteResellerUserDialog } from './InviteResellerUserDialog';

const SEARCH_DEBOUNCE_MS = 250;

interface ResellerUsersPanelProps {
  resellerId: number;
  /** Called after an invitation is sent so the Invitations tab refetches. */
  onInvited: () => void;
}

/** Info and errors stay a banner; quiet success is a toast (TMMPDM-0034). */
type Notice = { severity: 'info' | 'error'; message: string } | null;

export function ResellerUsersPanel({ resellerId, onInvited }: ResellerUsersPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${resellerId}:${query}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    members: ResellerMemberDto[] | null;
    failure: unknown;
  }>({ requestKey: null, members: null, failure: null });
  const [notice, setNotice] = useState<Notice>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ResellerMemberDto | null>(null);
  // A refused removal is said inside its confirm, which stays open (TMM-B4).
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    listResellerUsers(resellerId, { page: 0, size: 100, ...(query ? { q: query } : {}) })
      .then((page) => {
        if (!cancelled) setResponse({ requestKey, members: page.content, failure: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setResponse({ requestKey, members: null, failure: err });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, query, requestKey]);

  const current = response.requestKey === requestKey;
  const members = current ? response.members : null;

  const refetch = () => setReloadToken((n) => n + 1);

  const showFailure = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.code === 'RESELLER_CONSOLE_NOT_CONFIGURED') {
      setNotice({
        severity: 'info',
        message: t(
          'resellers.users.noConsole',
          "tmm has no reseller console yet, so reseller users can't be invited. The platform operator sets it up in saas-admin.",
        ),
      });
      return;
    }
    setNotice({ severity: 'error', message: errorCopy(err, t, fallback) });
  };

  const mutate = async (
    member: ResellerMemberDto,
    run: () => Promise<unknown>,
    fallback: string,
    report: (err: unknown, fallback: string) => void = showFailure,
  ): Promise<boolean> => {
    setNotice(null);
    setBusyId(member.id);
    try {
      await run();
      refetch();
      return true;
    } catch (err) {
      report(err, fallback);
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = () => {
    const member = removeTarget;
    if (!member) return;
    setRemoveError(null);
    void mutate(
      member,
      () => removeResellerUser(resellerId, member.id),
      t('resellers.users.removeError', 'Could not remove the user.'),
      (err, fallback) => setRemoveError(errorCopy(err, t, fallback)),
    ).then((removed) => {
      if (removed) setRemoveTarget(null);
    });
  };

  return (
    <Box>
      <PeopleToolbar>
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          label={t('resellers.users.searchLabel', 'Search users')}
          sx={{ minWidth: 260 }}
        />
        <Button variant="contained" onClick={() => setInviteOpen(true)}>
          {t('resellers.users.invite', 'Invite administrator…')}
        </Button>
      </PeopleToolbar>

      {notice ? (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      ) : null}

      {!current ? <LoadingSpinner /> : null}
      {current && members === null ? (
        <ErrorState
          error={response.failure}
          message={t('resellers.users.loadError', 'Could not load users.')}
          onRetry={refetch}
        />
      ) : null}

      {members ? (
        <PeopleTableContainer>
          <Table aria-label={t('resellers.users.tableAriaLabel', 'Reseller users')} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('resellers.users.col.email', 'Email')}</TableCell>
                <TableCell>{t('resellers.users.col.name', 'Name')}</TableCell>
                <TableCell>{t('resellers.users.col.role', 'Role')}</TableCell>
                <TableCell>{t('resellers.users.col.status', 'Status')}</TableCell>
                <TableCell>{t('resellers.users.col.mfa', 'MFA')}</TableCell>
                <TableCell>{t('resellers.users.col.password', 'Password')}</TableCell>
                <TableCell align="right">{t('resellers.col.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <EmptyState title={t('resellers.users.empty', 'No users yet.')} />
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => {
                  const busy = busyId === member.id;
                  const suspended = member.status === 'SUSPENDED';
                  return (
                    <TableRow key={member.id}>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.name ?? '—'}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          variant="standard"
                          label={t('resellers.users.roleFor', 'Role for {{email}}', {
                            email: member.email,
                          })}
                          value={member.role}
                          disabled={busy}
                          onChange={(e) =>
                            void mutate(
                              member,
                              () =>
                                changeResellerUserRole(
                                  resellerId,
                                  member.id,
                                  e.target.value as ResellerRole,
                                ),
                              t('resellers.users.roleError', 'Could not change the role.'),
                            )
                          }
                          sx={{ minWidth: 110 }}
                        >
                          {RESELLER_ROLES.map((r) => (
                            <MenuItem key={r} value={r}>
                              {t(`resellers.role.${r}`, r)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={member.status}
                          label={tenantStatusLabel(member.status, t)}
                        />
                      </TableCell>
                      <TableCell>
                        {member.mfaEnabled ? (
                          <Chip size="small" label={t('resellers.users.mfaOn', 'MFA')} />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {member.hasPassword ? (
                          <Chip size="small" label={t('resellers.users.passwordSet', 'Password')} />
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() =>
                            void mutate(
                              member,
                              () =>
                                setResellerUserStatus(
                                  resellerId,
                                  member.id,
                                  suspended ? 'ACTIVE' : 'SUSPENDED',
                                ),
                              t('resellers.users.statusError', 'Could not change the status.'),
                            )
                          }
                        >
                          {suspended
                            ? t('resellers.users.reactivate', 'Reactivate')
                            : t('resellers.users.suspend', 'Suspend')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => setRemoveTarget(member)}
                        >
                          {t('resellers.users.remove', 'Remove')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </PeopleTableContainer>
      ) : null}

      <InviteResellerUserDialog
        open={inviteOpen}
        resellerId={resellerId}
        onClose={() => setInviteOpen(false)}
        onInvited={() => {
          setInviteOpen(false);
          setNotice(null);
          notify.success(t('resellers.users.invited', 'Invitation sent.'));
          onInvited();
        }}
        onFailed={(err) => {
          setInviteOpen(false);
          showFailure(err, t('resellers.users.inviteError', 'Could not send the invitation.'));
        }}
      />
      <ConfirmDialog
        open={removeTarget !== null}
        title={t('resellers.users.removeTitle', 'Remove user?')}
        consequence={t(
          'resellers.users.removeBody',
          'This removes {{email}} from this reseller, with its password and sessions.',
          { email: removeTarget?.email ?? '' },
        )}
        confirmLabel={t('resellers.users.remove', 'Remove')}
        destructive
        busy={busyId !== null}
        error={removeError}
        onConfirm={handleRemove}
        onCancel={() => {
          setRemoveTarget(null);
          setRemoveError(null);
        }}
      />
    </Box>
  );
}
